import React, { useCallback, useEffect, useState } from 'react';
import { ProjectChatDetailApi } from '~/projects/store/project';
import { IssueDetails, ServiceDetails, CommunicationChatDetails } from "~/types";
import classes from './style.module.scss';
import useTranslation from "~/app/hooks/useTranslation";
import List from "./List";
import Form from "./Form";
import {useStoreActions, useStoreState} from "~/store";
import useWebSocket from "~/app/hooks/useWebSocket";
import {IssueStatuses} from "~/app/data/statuses/issue";
import {PERMISSION_GROUPS} from "~/accessControl/permissionGroups";
import AccessControl from "~/accessControl";
import { getChatStoreKey, MessageType, selectChatData } from '~/app/data/statuses/chat';
import { MaterialIcons as Icons } from '~/app/common/Icon/icons';
import Icon from '~/app/common/Icon';
import AddUserDialog from './AddUser';

export interface MessagesProps {
    issue?: IssueDetails | ServiceDetails | ProjectChatDetailApi | CommunicationChatDetails
    type: MessageType
    containerClassName?: string
    listClassName?: string
}

export type MessagesComponent = React.FC<MessagesProps>

const Messages: MessagesComponent = ({ issue, type, containerClassName, listClassName }) => {
    const basePath = getChatStoreKey(type);
    const {t} = useTranslation("issue.messages");
    const chatSlice = useStoreState(state => state[basePath]);
    const issueId = useStoreState(state => String(selectChatData(state, type).id ?? ''));
    const isReadOnlyChat = chatSlice?.isReadOnlyChat ?? false;
    const chat = useStoreState(state => selectChatData(state, type).chat ?? []);
    const chatUsers = useStoreState(state => selectChatData(state, type).chatUsers ?? []);
    const setTempChatUsersAction = useStoreActions((actions) => actions[basePath].setTempChatUsers);
    const chatUsersFetchAction = useStoreActions(actions => actions[basePath].fetchChatUsers);
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const updateNotificationsStatusAction = useStoreActions((actions) => actions.chat.updateNotificationsStatus);
    const { updateUnreadCount } = useWebSocket();
    const [isOpenAddUserForm, setIsOpenAddUserForm] = useState(false);

    const toggleAddUserDialog = useCallback((state: boolean) => (): void => {
        setIsOpenAddUserForm(state);
        if (!state) {
            setTempChatUsersAction(chatUsers);
        }
    }, [chatUsers, setTempChatUsersAction])
  
    useEffect(() => {
        if (netWorkStatus === 0 && issueId && Number(issueId) > 0) {
            updateNotificationsStatusAction({ issueId: issueId, type }).then(() => {
                updateUnreadCount();
            });
        }
    }, [netWorkStatus, chat, issueId, type, updateNotificationsStatusAction, updateUnreadCount]);
    
    useEffect(() => {
        if (!netWorkStatus && issueId && Number(issueId) > 0) {
            chatUsersFetchAction(issueId);
        }
    }, [issueId, chatUsersFetchAction, netWorkStatus]);
    
    const content = (
        <div className={[classes['messages-container'], containerClassName].filter(Boolean).join(' ')}>
            <div className={classes['title']}>
                <div className={classes['chat-header']}>
                    {t('title')}
                    <span className={classes['message-count']}>{chat.length}</span>
                </div>
                <div className={classes['add-user-container']}>
                    <button className={classes['add-user-button']} onClick={() => setIsOpenAddUserForm(!isOpenAddUserForm)} type="button">
                        <span>{t('users')} ({chatUsers.length})</span>
                        {!isReadOnlyChat && <Icon name={"add_circle" as Icons} className={classes.icon} />}
                    </button>
                </div>
            </div>
            {!netWorkStatus && <AddUserDialog open={isOpenAddUserForm} onClose={toggleAddUserDialog(false)} type={type}/>}
            <List type={type} className={listClassName}/>
            {netWorkStatus === 0 && !isReadOnlyChat && <Form type={type}/>}
        </div>
    );

    const issueStatus = issue && 'status' in issue ? issue.status : null;
    const shouldRenderAccessControl =
        (type === MessageType.ISSUE && (issueStatus === IssueStatuses.COMPLETED || issueStatus === IssueStatuses.CLOSED))
        || type === MessageType.SERVICE
        || type === MessageType.MACHINE;

    const requiredPermissions = type === MessageType.ISSUE
        ? PERMISSION_GROUPS.readCompletedIssueChat
        : type === MessageType.SERVICE
            ? PERMISSION_GROUPS.readServiceDetails
            : type === MessageType.MACHINE
                ? PERMISSION_GROUPS.machineRegistration
                : null;
    
    return shouldRenderAccessControl && requiredPermissions ? (
        <AccessControl requiredPermissions={requiredPermissions}>
            {content}
        </AccessControl>
    ) : content;
    
};

export default Messages
